//Components

import DialogDelete from '../components/DialogDelete/DialogDelete'
import Module from '../../../../components/layout/Module'
import DataGrid from '../../../../components/table/DataGrid'
import TextAuditLog from '../../../../components/text/TextAuditLog'
import IconTooltipButton from '../../../../components/button/IconTooltipButton'
import FormatDate from '../../../../components/date/FormatDate'
import ResponsiveChips from '../components/ResponsiveChips/ResponsiveChips'
import TextNote from '../../../text/TextNote'
import MenuButton from '../../../../components/button/MenuButton'
import Chip from '@mui/material/Chip'
import ExportResponsesButton from './ExportResponsesButton'

//Other dependencies dataEntryAssessment
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useMatch } from 'react-router-dom'
import { loadAssessments } from '../../../../store/actions/dataEntryAssessmentActions'
import { resetAssessmentMarkingState } from '../../../../store/actions/dataEntryAssessmentMarkingActions'
import { resetAssesmentBuilder } from '../../../../store/actions/dataEntryAssessmentBuilderActions'
import { updateSkip, updateRowsPerPage } from '../../../../store/actions/datatable.actions'
import { alertReset } from '../../../../store/actions/alert.actions'
import { deleteAssessment, cloneAssessment } from '../../../../services/data-entry/assessments.service'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { ASSESSMENT_TYPE_STANDARD, ASSESSMENT_TYPE_CUSTOM } from '../constants/typeAssessment'
import StatusChip from '../components/StatusChip/StatusChip'
import Filter from './FilterList/Filter'

const AssessmentsList = () => {
  const match = useMatch('/assessments')
  const url = match ? match.pathname : ''
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const currentModule = 'dataentryassessment'
  const [showDialog, setShowDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [cloningId, setCloningId] = useState(null)
  const permissions = useSelector((state) => state?.session?.permissions?.dataentryassessment ?? {})
  const list = useSelector((state) => state?.dataEntryAssessment?.assessments?.list ?? [])
  const loading = useSelector((state) => state?.dataEntryAssessment?.assessments?.loading ?? [])
  const totalRecords = useSelector((state) => state?.dataEntryAssessment?.assessments?.totalRecords ?? [])
  const skip = useSelector((state) => state?.datatable?.assessmentList?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.assessmentList?.rowsPerPage || 100)
  const rowsPerPageAssessmentMarking = useSelector(
    (state) => state?.datatable?.assessmentMarking?.rowsPerPage ?? 100
  )
  const rowsPerPageAssessmentStudentsList = useSelector(
    (state) => state?.datatable?.assessmentStudentsList?.rowsPerPage ?? 100
  )
  const columnsTable = useSelector((state) => state?.datatable?.assessmentList?.columnsTable || [])
  const state = useSelector((state) => state?.filter?.dataEntryAssessment || {})

  const onLoadData = useCallback(() => {
    const filter = { ...state?.filter }
    delete filter?.template
    delete filter?.yearLevel

    dispatch(loadAssessments(skip * rowsPerPage, rowsPerPage, filter))
    dispatch(alertReset())
  }, [skip, rowsPerPage, state?.filter, dispatch])

  useEffect(() => {
    onLoadData()
  }, [onLoadData])

  const cloneExistingAssessment = async (assessmentId) => {
    // El clone duplica el assessment + genera el marking de todos los alumnos, así
    // que puede tardar varios segundos. Sin feedback el botón parece muerto (QA
    // MF-475). Avisamos de entrada y marcamos el botón como "en progreso".
    setCloningId(assessmentId)
    enqueueSnackbar('Cloning assessment… this may take a few seconds', {
      variant: 'info'
    })

    try {
      const { error } = await cloneAssessment(assessmentId)

      if (error) {
        enqueueSnackbar('We could not make a copy of this assessment', {
          variant: 'error'
        })
      } else {
        enqueueSnackbar('Assessment cloned successfully', {
          variant: 'success'
        })
        setSelectedRow(null)
        onLoadData()
      }
    } catch (error) {
      enqueueSnackbar('We could not make a copy of this assessment', {
        variant: 'error'
      })
    } finally {
      setCloningId(null)
    }
  }

  const setCurrentRow = async (row, action) => {
    const data = JSON.parse(JSON.stringify(list.find((x) => x._id === row._id)))

    if (data) {
      if ('marking' === action) {
        setSelectedRow(data)
        dispatch(resetAssessmentMarkingState())
        dispatch(updateSkip('assessmentMarking', 0))
        dispatch(updateRowsPerPage('assessmentMarking', rowsPerPageAssessmentMarking))
        navigate(`/dashboard/data-entry/assessment-marking/${row._id}`, {
          state: { from: '/dashboard/data-entry/assessments' }
        })
      }

      if ('delete' === action) {
        setSelectedRow(data)
        setShowDialog(true)
      }

      if ('edit' === action) {
        dispatch(updateSkip('assessmentStudentsList', 0))
        dispatch(updateRowsPerPage('assessmentStudentsList', rowsPerPageAssessmentStudentsList))
        dispatch(resetAssesmentBuilder())
        navigate(`/dashboard/data-entry/assessments/${row._id}/edit/${row?.type}`)
      }

      if ('clone' === action) {
        cloneExistingAssessment(data._id)
      }
    }
  }

  const onCancelDialog = () => {
    setShowDialog(false)
    setSelectedRow(null)
  }

  const onConfirmDialog = async () => {
    try {
      setIsDeleting(true)

      const { error } = await deleteAssessment(selectedRow?._id)

      if (error) {
        enqueueSnackbar('We could not delete the assessment', {
          variant: 'error'
        })
      } else {
        onLoadData()
      }
    } catch (error) {
      enqueueSnackbar('We could not delete the assessment', {
        variant: 'error'
      })
    } finally {
      setIsDeleting(false)
      setShowDialog(false)
    }
  }

  const redirect = (redirectTo) => {
    navigate(`${url}/${redirectTo}`)
  }

  const onCreateAssessment = (type) => {
    dispatch(updateSkip('assessmentStudentsList', 0))
    dispatch(updateRowsPerPage('assessmentStudentsList', 50))
    dispatch(resetAssesmentBuilder())

    redirect(`dashboard/data-entry/assessments/add/${type}`)
  }

  const getUniqueClasses = (classes = []) => {
    //TODO: For now unique by code, beacuse we don't know if class ids can change every time the data is imported
    return Array.from(new Map(classes?.map((item) => [item?.code, item])).values())
  }

  const options = useMemo(() => {
    let options = []
    if (permissions?.create) {
      options.push({
        id: ASSESSMENT_TYPE_STANDARD,
        label: 'Standard assessment',
        permissions: true,
        onClick: () => onCreateAssessment(ASSESSMENT_TYPE_STANDARD)
      })

      options.push({
        id: ASSESSMENT_TYPE_CUSTOM,
        label: 'Custom assessment',
        permissions: true,
        divider: true,
        onClick: () => onCreateAssessment(ASSESSMENT_TYPE_CUSTOM)
      })
    }

    return options
  }, [permissions])

  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title='Assessments'
        titleLink={'/dashboard/data-entry/assessments'}
      >
        <div className='flex justify-end items-center gap-2'>
          {permissions?.create && (
            <MenuButton
              id='data-entry-assessments-create'
              icon='circle-check'
              title='Create Assessment'
              color='secondary'
              items={options}
            />
          )}
        </div>
      </Module.Header>
      <Module.BodyHeader py='4' className='border-b border-b-2 border-primary'>
        <Filter />
      </Module.BodyHeader>
      <Module.BodyContainer className='overflow-y-auto'>
        <DataGrid
          className='pb-6'
          id='table-assessments'
          rows={list.map((row, index) => ({
            ...row,
            index: index + 1,
            name: row.name ?? '',
            yearLevel: (
              <ResponsiveChips
                minDisplay={3}
                maxDisplay={10}
                chipVariant='outlined'
                items={row?.template?.yearLevels ?? []}
                field='name'
              />
            ),
            class: (
              <ResponsiveChips
                minDisplay={3}
                maxDisplay={20}
                items={getUniqueClasses(row?.classes)}
                field='code'
              />
            ),
            template: row?.template?.title ?? '',
            type: (
              <Chip
                label={row?.type?.replace(/-/g, ' ')}
                size='small'
                className='uppercase'
                color={row?.type === ASSESSMENT_TYPE_STANDARD ? 'primary' : 'secondary'}
              />
            ),
            sittingDate: row?.sittingDate ? <FormatDate date={row?.sittingDate} /> : null,
            dueDate: row?.dueDate ? <FormatDate date={row?.dueDate} /> : null,
            status: (
              <StatusChip
                content={row?.status ?? 'Closed'}
                type={row?.status === 'Closed' ? 'error' : 'done'}
              />
            ),
            date: row?.date ? <FormatDate date={row?.date} /> : null,
            log: (
              <TextAuditLog
                updatedAt={row.updatedAt}
                updatedBy={row.updatedBy}
                createdBy={row.createdBy}
                createdAt={row.createdAt}
              />
            ),
            note: <TextNote notes={row.notes || ''} />,
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions?.marking && (
                  <IconTooltipButton
                    id={`marking-${row._id}`}
                    disabled={loading}
                    title='Marking'
                    icon='pencil-mechanical'
                    onClick={() => setCurrentRow(row, 'marking')}
                  />
                )}
                {permissions?.edit && (
                  <IconTooltipButton
                    id={`edit-${row._id}`}
                    disabled={loading}
                    title='Edit'
                    icon='pencil'
                    onClick={() => setCurrentRow(row, 'edit')}
                  />
                )}
                {permissions?.create && (
                  <IconTooltipButton
                    id={`clone-${row._id}`}
                    disabled={loading || !!cloningId}
                    title={cloningId === row._id ? 'Cloning… this may take a few seconds' : 'Make a copy'}
                    icon={cloningId === row._id ? 'circle-notch' : 'clone'}
                    spin={cloningId === row._id}
                    onClick={() => setCurrentRow(row, 'clone')}
                  />
                )}

                {permissions['show-one'] && (
                  <ExportResponsesButton
                    survey={row}
                    surveyId={row?._id}
                    isExporting={isExporting}
                    setIsExporting={setIsExporting}
                  />
                )}

                {permissions?.delete && (
                  <IconTooltipButton
                    id={`delete-${row._id}`}
                    disabled={loading}
                    title='Delete'
                    icon='fa-trash'
                    onClick={() => setCurrentRow(row, 'delete')}
                  />
                )}
              </div>
            )
          }))}
          loading={loading}
          labelNoRows='No assessments found'
          nameModule='assessmentList'
          headers={[...columnsTable]}
          totalRows={totalRecords}
          skip={skip}
          rowsPerPage={rowsPerPage}
          columns={[
            {
              label: '#',
              key: 'index',
              align: 'center',
              width: 10,
              show: true,
              hide: false
            },
            ...columnsTable,
            {
              label: 'Actions',
              key: 'actions',
              align: 'center',
              show: true,
              hide: false
            }
          ]}
          orderByField='name'
        />
      </Module.BodyContainer>
      <DialogDelete
        show={showDialog}
        onCancel={onCancelDialog}
        onConfirm={onConfirmDialog}
        loading={isDeleting}
        type='delete'
        item={selectedRow?.name}
        module='assessment'
      />
    </Module>
  )
}
export default AssessmentsList
